import { computed } from 'vue'
import type { RequirementsState } from './state'
import type { Requirement, DefinitionStatus, DevelopmentStatus, Priority } from '@/types'

export const createGetters = (state: RequirementsState) => {
  const filteredRequirements = computed(() => {
    let result = [...state.requirements]

    // Filter by search
    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (req) =>
          req.title.toLowerCase().includes(search) ||
          req.key?.toLowerCase().includes(search) ||
          req.description.toLowerCase().includes(search)
      )
    }

    // Filter by project
    if (state.filters.projectId) {
      result = result.filter((req) => req.projectId === state.filters.projectId)
    }

    // Filter by application
    if (state.filters.applicationId) {
      result = result.filter((req) => req.applicationId === state.filters.applicationId)
    }

    // Filter by epic
    if (state.filters.epicId) {
      result = result.filter((req) => req.epicId === state.filters.epicId)
    }

    // Filter by no epic
    if (state.filters.hasNoEpic) {
      result = result.filter((req) => !req.epicId)
    }

    // Filter by type
    if (state.filters.type) {
      result = result.filter((req) => req.type === state.filters.type)
    }

    // Filter by definition status
    if (state.filters.definitionStatus) {
      result = result.filter((req) => req.definitionStatus === state.filters.definitionStatus)
    }

    // Filter by development status
    if (state.filters.developmentStatus) {
      result = result.filter((req) => req.developmentStatus === state.filters.developmentStatus)
    }

    // Filter by priority
    if (state.filters.priority) {
      result = result.filter((req) => req.priority === state.filters.priority)
    }

    // Filter by assigned to
    if (state.filters.assignedTo) {
      result = result.filter((req) => req.assignedTo === state.filters.assignedTo)
    }

    return result
  })

  const requirementsByDefinitionStatus = computed(() => {
    const grouped: Record<DefinitionStatus, Requirement[]> = {
      requested: [],
      analyzing: [],
      defining: [],
      costing: [],
      approved: [],
      rejected: [],
    }

    state.requirements.forEach((req) => {
      if (grouped[req.definitionStatus]) {
        grouped[req.definitionStatus].push(req)
      }
    })

    return grouped
  })

  const requirementsByDevelopmentStatus = computed(() => {
    const grouped: Record<DevelopmentStatus, Requirement[]> = {
      backlog: [],
      design: [],
      development: [],
      code_review: [],
      qa: [],
      uat: [],
      production: [],
    }

    state.requirements.forEach((req) => {
      if (req.developmentStatus && grouped[req.developmentStatus]) {
        grouped[req.developmentStatus].push(req)
      }
    })

    return grouped
  })

  const requirementsByPriority = computed(() => {
    const grouped: Record<Priority, Requirement[]> = {
      critical: [],
      high: [],
      medium: [],
      low: [],
    }

    state.requirements.forEach((req) => {
      if (grouped[req.priority]) {
        grouped[req.priority].push(req)
      }
    })

    return grouped
  })

  const requirementsByProject = computed(() => {
    const grouped: Record<string, Requirement[]> = {}

    state.requirements.forEach((req) => {
      if (!grouped[req.projectId]) {
        grouped[req.projectId] = []
      }
      grouped[req.projectId].push(req)
    })

    return grouped
  })

  const requirementsByEpic = computed(() => {
    const grouped: Record<string, Requirement[]> = {}

    state.requirements.forEach((req) => {
      const epicKey = req.epicId || 'no_epic'
      if (!grouped[epicKey]) {
        grouped[epicKey] = []
      }
      grouped[epicKey].push(req)
    })

    return grouped
  })

  const approvedRequirements = computed(() => {
    return state.requirements.filter((req) => req.definitionStatus === 'approved')
  })

  const pendingRequirements = computed(() => {
    return state.requirements.filter(
      (req) => req.definitionStatus !== 'approved' && req.definitionStatus !== 'rejected'
    )
  })

  const inProgressRequirements = computed(() => {
    return state.requirements.filter(
      (req) => req.developmentStatus === 'development' || req.developmentStatus === 'code_review'
    )
  })

  const doneRequirements = computed(() => {
    return state.requirements.filter((req) => req.developmentStatus === 'production')
  })

  const inQARequirements = computed(() => {
    return state.requirements.filter(
      (req) => req.developmentStatus === 'qa' || req.developmentStatus === 'uat'
    )
  })

  const backlogRequirements = computed(() => {
    return state.requirements.filter(
      (req) => req.developmentStatus === 'backlog' || req.developmentStatus === 'design'
    )
  })

  // Story points calculations
  const totalStoryPoints = computed(() => {
    return state.requirements.reduce((sum, req) => sum + (req.storyPoints || 0), 0)
  })

  const completedStoryPoints = computed(() => {
    return doneRequirements.value.reduce((sum, req) => sum + (req.storyPoints || 0), 0)
  })

  const stats = computed(() => {
    const total = state.requirements.length
    const pending = pendingRequirements.value.length
    const approved = approvedRequirements.value.length
    const inProgress = inProgressRequirements.value.length
    const inQA = inQARequirements.value.length
    const done = doneRequirements.value.length
    const critical = state.requirements.filter((r) => r.priority === 'critical').length
    const high = state.requirements.filter((r) => r.priority === 'high').length

    return {
      total,
      pending,
      approved,
      inProgress,
      inQA,
      done,
      critical,
      high,
      totalStoryPoints: totalStoryPoints.value,
      completedStoryPoints: completedStoryPoints.value,
      progressPercentage: total > 0 ? Math.round((done / total) * 100) : 0,
    }
  })

  const getRequirementById = (id: string): Requirement | undefined => {
    return state.requirements.find((req) => req.id === id)
  }

  const getRequirementByKey = (key: string): Requirement | undefined => {
    return state.requirements.find((req) => req.key === key)
  }

  const getRequirementsByProjectId = (projectId: string): Requirement[] => {
    return state.requirements.filter((req) => req.projectId === projectId)
  }

  const getRequirementsByApplicationId = (applicationId: string): Requirement[] => {
    return state.requirements.filter((req) => req.applicationId === applicationId)
  }

  const getRequirementsByEpicId = (epicId: string): Requirement[] => {
    return state.requirements.filter((req) => req.epicId === epicId)
  }

  const getRequirementsByReleaseId = (releaseId: string): Requirement[] => {
    return state.requirements.filter((req) => req.releaseId === releaseId)
  }

  const getSubtasks = (parentId: string): Requirement[] => {
    return state.requirements.filter((req) => req.parentId === parentId)
  }

  // Calculate progress for an epic
  const getEpicProgress = (epicId: string) => {
    const epicRequirements = getRequirementsByEpicId(epicId)
    if (epicRequirements.length === 0) return { total: 0, done: 0, percentage: 0 }

    const done = epicRequirements.filter((r) => r.developmentStatus === 'production').length
    return {
      total: epicRequirements.length,
      done,
      percentage: Math.round((done / epicRequirements.length) * 100),
    }
  }

  // Calculate progress for a project
  const getProjectProgress = (projectId: string) => {
    const projectRequirements = getRequirementsByProjectId(projectId)
    if (projectRequirements.length === 0) return { total: 0, done: 0, percentage: 0, storyPoints: { total: 0, done: 0 } }

    const done = projectRequirements.filter((r) => r.developmentStatus === 'production').length
    const totalSP = projectRequirements.reduce((sum, r) => sum + (r.storyPoints || 0), 0)
    const doneSP = projectRequirements
      .filter((r) => r.developmentStatus === 'production')
      .reduce((sum, r) => sum + (r.storyPoints || 0), 0)

    return {
      total: projectRequirements.length,
      done,
      percentage: Math.round((done / projectRequirements.length) * 100),
      storyPoints: {
        total: totalSP,
        done: doneSP,
        percentage: totalSP > 0 ? Math.round((doneSP / totalSP) * 100) : 0,
      },
    }
  }

  return {
    filteredRequirements,
    requirementsByDefinitionStatus,
    requirementsByDevelopmentStatus,
    requirementsByPriority,
    requirementsByProject,
    requirementsByEpic,
    approvedRequirements,
    pendingRequirements,
    inProgressRequirements,
    inQARequirements,
    doneRequirements,
    backlogRequirements,
    totalStoryPoints,
    completedStoryPoints,
    stats,
    getRequirementById,
    getRequirementByKey,
    getRequirementsByProjectId,
    getRequirementsByApplicationId,
    getRequirementsByEpicId,
    getRequirementsByReleaseId,
    getSubtasks,
    getEpicProgress,
    getProjectProgress,
  }
}
